<template>
  <v-app>

    <!-- global loader -->
    <fullpage-loader
      :message="loadingMessage"
      v-if="loadingMessage"
    />

    <navigation-drawer v-if="user && !loadingMessage" />
    <app-bar v-if="showAppBar && user && !loadingMessage" />

    <v-content v-if="user && !loadingMessage">
      <!-- Loader -->
      <v-container
        fluid
        v-if="fetchingGeneralData"
      >
        <v-row>
          <v-col>
            <v-progress-linear
              indeterminate
              height="2"
            ></v-progress-linear>
          </v-col>
        </v-row>
      </v-container>

      <!-- Content -->
      <router-view v-if="generalDataFetched && !fetchingGeneralData" />
    </v-content>

    <!-- Auth -->
    <auth v-if="!user && !loadingMessage" />

    <!-- Dialogs -->
    <bug-report-dialog />
    <entity-edit-dialog v-if="entityEditDialog" />

    <!-- Global Snackbar -->
    <snackbar />

    <!-- Confirm -->
    <confirm ref="confirm"></confirm>
  </v-app>
</template>

<script>
import AppBar from "@/components/AppBar";
import NavigationDrawer from "@/components/NavigationDrawer";
import Confirm from "@/components/Confirm";
import BugReportDialog from "@/components/BugReportDialog";
import EntityEditDialog from "@/components/EntityEditDialog";
import Snackbar from "@/components/Snackbar";
import Auth from "@/components/Auth";
import FullpageLoader from "@/components/FullpageLoader";

// For socket io init
import VueSocketIO from "vue-socket.io";
import socketio from "socket.io-client";
import Vue from "vue";

// import jwtDecode from "jwt-decode";
// import firebaseAuth from "firebase/auth";
import firebase from "firebase/app";

import { getApi } from "./api";

export default {
  data: () => ({
    fetchingGeneralData: false,
    generalDataFetched: false,
    checkingIfFirebaseUserExists: false,
    gettingFirebaseUserData: false
  }),

  computed: {
    nightMode() {
      return this.$store.getters["nightMode"];
    },
    entityEditDialog: {
      get() {
        return this.$store.state.entityEditDialog;
      },
      set(payload) {
        this.$store.commit("TOGGLE_ENTITY_EDIT_DIALOG", payload);
      }
    },
    showAppBar() {
      return this.$route.path !== "/login";
    },
    user() {
      return this.$store.getters["user/user"];
    },
    firebaseUser() {
      return this.$store.getters["user/firebaseUser"];
    },
    loadingMessage() {
      if (this.checkingIfFirebaseUserExists) {
        return "מחפש משתמש מחובר...";
      } else if (this.gettingFirebaseUserData) {
        return "מאתחל מידע של המשתמש...";
      } else {
        return false;
      }
    }
  },

  methods: {
    initApp() {
      this.fetchGeneralData();
      getApi();
    },
    fetchGeneralData() {
      this.fetchingGeneralData = true;
      let funcArray = [];
      let resourcesNames = [
        "user",
        "taskType",
        "boxType",
        "storeType",
        "store",
        "deviceType",
        "emailType",
        "smsType",
        "agentType",
        "coupon",
        "returnType",
        "receiveType",
        "orderStatus",
        "orderItemType",
        "device",
        "addon",
        "overragePrice",
        "role",
        "networkStatusType"
      ];
      for (let resourceName of resourcesNames) {
        let resource = this.$store.state.resources[resourceName];
        funcArray.push(
          this.$store.dispatch("API_GET", {
            url: resource.endpoint,
            entityType: resource.entityType,
            schema: resource.listSchema,
            params: Object.assign({ limit: 0 }, resource.getParams)
          })
        );
      }

      Promise.all(funcArray)
        .then(() => {
          let orderStatuses = Object.values(
            this.$store.state.entities.orderStatuses
          );
          let paid = orderStatuses.find(item => {
            return item.key === "PAID";
          });
          let bookedOut = orderStatuses.find(item => {
            return item.key === "BOOKED_OUT";
          });
          let checkedIn = orderStatuses.find(item => {
            return item.key === "CHECKED_IN";
          });

          return this.$store.dispatch("API_GET", {
            url: "/orders",
            entityType: "orders",
            schema: "orderListSchema",
            params: {
              limit: 0,
              orderStatuses: `${paid._id},${bookedOut._id},${checkedIn._id}`
            }
          });
        })
        .then(() => {
          this.generalDataFetched = true;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.fetchingGeneralData = false;
        });
    },
    firebaseAuth() {
      let self = this;
      let interval;
      this.checkingIfFirebaseUserExists = true;

      firebase.auth().onIdTokenChanged(function(firebaseUser) {
        self.checkingIfFirebaseUserExists = false;
        if (firebaseUser) {
          let user = self.$store.getters["user/user"];
          if (!user) {
            self.gettingFirebaseUserData = true;
            firebaseUser
              .getIdToken()
              .then(idToken => {
                self.$store.commit("user/SET_FIREBASE_USER", firebaseUser);
                self.$store
                  .dispatch("user/GET_USER_BY_FIREBASE_ID", firebaseUser.uid)
                  .catch(err => {
                    console.error(err);
                    self.$store.dispatch("user/SIGN_OUT");
                    self.$store.dispatch("SHOW_SNACKBAR", {
                      text: "המשתמש לא מוגדר במערכת. יש ליצור קשר עם מנהל",
                      color: "error"
                    });
                  })
                  .finally(() => {
                    self.connectSocket(idToken);
                    self.gettingFirebaseUserData = false;
                  });
              })
              .catch(err => {
                console.error(err);
                self.$store.commit("user/SET_FIREBASE_USER", null);
              });
          }
        } else {
          // No user is signed in.
          if (interval) {
            clearInterval(interval);
            interval = null;
          }

          self.$store.commit("user/SET_FIREBASE_USER", null);
        }
      });
    },
    connectSocket(token) {
      const socketInstance = socketio.connect(
        process.env.VUE_APP_SERVER_BASE_URL,
        {
          query: {
            token
          }
        }
      );
      Vue.use(
        new VueSocketIO({
          debug: process.env.NODE_ENV === "development",
          connection: socketInstance,
          vuex: {
            store: this.$store,
            actionPrefix: "SOCKET_",
            mutationPrefix: "SOCKET_"
          }
        })
      );
    }
  },

  sockets: {},

  mounted() {
    this.firebaseAuth();
    this.$root.$confirm = this.$refs.confirm.open;
  },

  watch: {
    user: function(val) {
      if (val) {
        this.initApp();
      }
    }
  },

  components: {
    AppBar,
    NavigationDrawer,
    BugReportDialog,
    EntityEditDialog,
    Snackbar,
    Confirm,
    Auth,
    FullpageLoader
  }
};
</script>

<style lang="scss">
html {
  unicode-bidi: plaintext !important;
}

.fullscreen {
  width: 100%;
  height: 100%;
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  overflow-y: scroll;
}

/* RTL FIX */

.v-application--is-rtl {
  .v-list-item__avatar:first-child {
    margin-right: unset;
    margin-left: 24px;
  }

  .v-list-item__action:first-child {
    margin-right: unset;
    margin-left: 36px;
  }

  .v-navigation-drawer--mini-variant {
    .v-list-item__avatar:first-child {
      margin-right: 0;
      margin-left: 0;
    }

    .v-list-item__action:first-child {
      margin-right: 0;
      margin-left: 0;
    }
  }

  .flip-horizontal {
    -moz-transform: scaleX(-1);
    -webkit-transform: scaleX(-1);
    -o-transform: scaleX(-1);
    transform: scaleX(-1);
    -ms-filter: fliph; /*IE*/
    filter: fliph; /*IE*/
  }
}
</style>

<style>
.v-application ul,
.v-application ol {
  padding-right: 24px;
}
</style>